import { Component, EventEmitter, Inject, Input, OnInit, Output } from '@angular/core';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { ConfigurationOptions, ConfigurationOptionType } from '@ervo/page-framework';
import { UntypedFormGroup } from '@angular/forms';
import { isHttps, isValidEmail, isValidUrl } from '@common/utils/formly-utils';
import { isInTrustedDomain } from '@common/utils/security.utils';
import { NAVIGATION_ITEM_ACTIONS, NavigationItemActions } from '@/app/platform/platform-data/actions/types';
import { NavigationItemEntity } from '@/app/platform/platform-data/models/navigation-item.entity';

const defaultConfigurationOptions: ConfigurationOptions = {
  wiki: {
    type: ConfigurationOptionType.URL,
    required: false
  }
};

const typeMap = {
  [ConfigurationOptionType.Boolean]: 'checkbox',
  [ConfigurationOptionType.String]: 'input',
  [ConfigurationOptionType.Int]: 'input',
  [ConfigurationOptionType.Double]: 'input',
  [ConfigurationOptionType.Email]: 'input',
  [ConfigurationOptionType.Password]: 'input',
  [ConfigurationOptionType.URL]: 'input'
};

const validatorMap = {
  [ConfigurationOptionType.URL]: {
    url: {
      expression: isValidUrl,
      message: () => 'Geen geldige URL!'
    },
    isTrusted: {
      expression: f => f.value ? isInTrustedDomain(f?.value) : true,
      message: () => 'Deze URL is niet herkend als vertrouwd. Contacteer Egi Support!'
    },
    isHttps: {
      expression: isHttps,
      message: () => 'HTTPS is vereist!'
    }
  },
  [ConfigurationOptionType.Email]: {
    email: {
      expression: isValidEmail,
      message: () => 'Geen geldige email!'
    }
  }
};

@Component({
    selector: 'navigation-item-configurator[navigationItem]',
    templateUrl: './navigation-item-configurator.component.html',
    styleUrls: ['./navigation-item-configurator.component.scss'],
    standalone: false
})
export class NavigationItemConfiguratorComponent implements OnInit {
  @Input() navigationItem: NavigationItemEntity;
  @Output() ready = new EventEmitter<never>();
  @Output() cancel = new EventEmitter<never>();

  constructor(
    @Inject(NAVIGATION_ITEM_ACTIONS)
    private readonly navigationItemActions: NavigationItemActions
  ) {}

  fields: FormlyFieldConfig[] = [];
  form = new UntypedFormGroup({});
  model = {};
  isLoading = false;

  get canSubmit(): boolean {
    return this.form.dirty && this.form.valid && !this.isLoading;
  }

  ngOnInit(): void {
    if (this.navigationItem.config) {
      this.model = this.navigationItem.config;
    }
    this.buildForm();
  }

  async onSubmit() {
    const n = this.navigationItem.clone();
    n.config = this.model;
    this.isLoading = true;
    const result = await this.navigationItemActions.changeConfiguration(n);
    this.isLoading = false;
    result.ifSuccess(() => {
      this.ready.emit();
    });
  }

  private buildForm(): void {
    const { configurationOptions } = this.navigationItem.egiPage.config;

    const options = { ...defaultConfigurationOptions, ...configurationOptions };

    Object.keys(options).forEach(key => {
      const option = options[key];

      const field: FormlyFieldConfig = {
        key,
        type: typeMap[option.type],
        templateOptions: {
          label: key
        }
      };

      if (option.required) {
        field.templateOptions.required = true;
      }

      if (
        option.type === ConfigurationOptionType.Int ||
        option.type === ConfigurationOptionType.Double
      ) {
        field.templateOptions.type = 'number';
      }

      if (option.type === ConfigurationOptionType.Password) {
        field.templateOptions.type = 'password';
      }

      const validator = validatorMap[option.type];
      if (validator) {
        field.validators = validator;
      }

      this.fields.push(field);
    });
  }
}
